import { PopupButtonType, PopupIconType, showPopup, Touchable } from "@kalyzee/kast-app-web-components";
import React, { useCallback, useEffect, useState } from "react";
import { showSubtitlesCostPopup } from "../../../components/subtitles/SubtitlesCostPopup";
import { OvenMediaSocketResultEmittedCommand } from "../../../helpers/socket";
import { CONTAINER_STATE_LABELS, getContainerStateColor, getSubtitlesStatusColor, getSubtitlesStatusLabel } from "../../../helpers/subtitles";
import { toastError, toastSuccess } from "../../../helpers/toast";
import { useSocketAppDispatch } from "../../../hooks/app";
import { OvenMediaSubtitlesStatus } from "../../../interfaces/context";
import { OvenMediaSubtitlesRunAllResponse, OvenMediaSubtitlesState, OvenMediaSubtitlesStopResponse } from "../../../interfaces/subtitles";
import { useOvenMediaContexts } from "../../../store/context/hooks";
import { socketGetSubtitlesState, socketSubtitlesRunAll, socketSubtitlesStop } from "../../../store/socket/actions";
import styles from "./subtitles.module.css";

// The « Sous-titres » panel of one server: the static part from the context (configuration, what
// the container is), the live part from `get_subtitles_state` on open and on refresh, and the two
// server-wide levers — « Tout transcrire maintenant » (level 1 only: forcing past a class is a
// per-record decision) and « Arrêter ».

const QUEUE_LABELS: Record<string, string> = {
  waiting: "en attente" /* TRANSLATION */,
  delayed: "différés (garés)" /* TRANSLATION */,
  prioritized: "prioritaires" /* TRANSLATION */,
  active: "actif" /* TRANSLATION */,
  completed: "terminés" /* TRANSLATION */,
  failed: "échoués" /* TRANSLATION */,
  paused: "en pause" /* TRANSLATION */,
};

export interface DashboardSubtitlesContainerProps {
  serverId: string;
  showServer?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const DashboardSubtitlesContainer = ({ serverId, showServer, className, style }: DashboardSubtitlesContainerProps) => {
  const contexts = useOvenMediaContexts();
  const context = contexts.find((c) => c.server._id === serverId);
  const block = context?.subtitles;
  const socketDispatch = useSocketAppDispatch();
  const [state, setState] = useState<OvenMediaSubtitlesState>();
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!block?.configured) return;
    setLoading(true);
    const result = await socketDispatch<OvenMediaSocketResultEmittedCommand<any, OvenMediaSubtitlesState>>(socketGetSubtitlesState({ serverId }));
    if (!result || result.error) toastError(`Sous-titres : ${result?.error?.message ?? result?.error ?? "pas de réponse"}` /* TRANSLATION */);
    else setState(result.response);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId, block?.configured]);

  useEffect(() => {
    load();
  }, [load]);

  const runAll = () => {
    showSubtitlesCostPopup({
      model: block?.model,
      scope: "all",
      allowBusy: false,
      onConfirm: async () => {
        const result = await socketDispatch<OvenMediaSocketResultEmittedCommand<any, OvenMediaSubtitlesRunAllResponse>>(socketSubtitlesRunAll({ serverId }));
        if (!result || result.error || !result.response) {
          toastError(`Sous-titres : ${result?.error?.message ?? result?.error ?? "pas de réponse"}` /* TRANSLATION */);
          return;
        }
        const { updated, promoted, override } = result.response;
        toastSuccess(
          `${updated} transcription(s) forcée(s), ${promoted} relancée(s) sans attendre — jusqu'à ${new Date(override.until).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.` /* TRANSLATION */
        );
        load();
      },
    });
  };

  const stop = () => {
    showPopup({
      title: "Arrêter la transcription" /* TRANSLATION */,
      iconTitle: PopupIconType.WARNING,
      content: (
        <div>
          <div className={styles.popupText}>
            {"Le conteneur Whisper est arrêté tout de suite, la transcription en cours est mise en attente (elle reprendra d'elle-même quand la machine sera libre), et tous les forçages sont annulés." /* TRANSLATION */}
          </div>
        </div>
      ),
      buttons: [
        { type: PopupButtonType.CANCEL, element: "Non" },
        {
          type: PopupButtonType.VALIDATE,
          element: "Oui, arrêter" /* TRANSLATION */,
          onClick: async () => {
            const result = await socketDispatch<OvenMediaSocketResultEmittedCommand<any, OvenMediaSubtitlesStopResponse>>(socketSubtitlesStop({ serverId }));
            if (!result || result.error || !result.response) {
              toastError(`Sous-titres : ${result?.error?.message ?? result?.error ?? "pas de réponse"}` /* TRANSLATION */);
              return true;
            }
            const { parked, container, overridesCleared } = result.response;
            toastSuccess(
              `${container ? "Conteneur arrêté" : "Conteneur déjà arrêté"}, ${parked ? "transcription mise en attente" : "aucune transcription en cours"}, ${overridesCleared} forçage(s) annulé(s).` /* TRANSLATION */
            );
            load();
            return true;
          },
        },
      ],
      enableBackdropDismiss: true,
      enableCloseButton: true,
    });
  };

  const classes = [styles.container];
  if (className) classes.push(className);

  const renderRow = (key: string, value: React.ReactNode) =>
    value === undefined || value === null || value === "" ? null : (
      <div className={styles.row} key={key}>
        <span className={styles.key}>{key}</span>
        <span className={styles.value}>{value}</span>
      </div>
    );

  const renderStatic = () => {
    if (!block) return <div className={styles.muted}>{"État inconnu : ce serveur ne décrit pas ses sous-titres (middleware trop ancien ?)." /* TRANSLATION */}</div>;
    if (!block.configured) return <div className={styles.muted}>{"Non configuré sur ce serveur (pas d'adresse Whisper)." /* TRANSLATION */}</div>;
    return (
      <>
        {renderRow(
          "Conteneur" /* TRANSLATION */,
          block.container ? `${block.container}${block.installed === false ? " — factice (pas de GPU)" : block.installed ? " — Whisper" : ""}` : "service externe, toujours actif"
        )}
        {renderRow("Modèle" /* TRANSLATION */, block.model)}
        {renderRow("Image" /* TRANSLATION */, block.image)}
        {renderRow("Langue" /* TRANSLATION */, block.language)}
        {renderRow("Fenêtre" /* TRANSLATION */, block.window ? `${block.window.start} – ${block.window.end}` : "aucune (à tout moment hors flux)")}
      </>
    );
  };

  const renderLive = () => {
    if (!block?.configured) return null;
    if (!state) return <div className={styles.muted}>{loading ? "Chargement…" : "État non chargé." /* TRANSLATION */}</div>;
    const running = state.running;
    const until = running?.override ? new Date(running.override.until) : undefined;
    return (
      <>
        {renderRow(
          "Conteneur" /* TRANSLATION */,
          state.container ? <span style={{ color: getContainerStateColor(state.container.state), fontWeight: "bold" }}>{CONTAINER_STATE_LABELS[state.container.state] ?? state.container.state}</span> : undefined
        )}
        {renderRow(
          "Machine" /* TRANSLATION */,
          state.idle ? (
            state.idle.idle ? (
              <span style={{ color: "var(--color-main-green)" }}>{"disponible" /* TRANSLATION */}</span>
            ) : (
              <span style={{ color: "var(--color-tree-poppy, #ff9820)" }}>{`occupée — ${state.idle.reason ?? ""}` /* TRANSLATION */}</span>
            )
          ) : undefined
        )}
        {state.stopRequested ? renderRow("Arrêt" /* TRANSLATION */, "demandé, en cours" /* TRANSLATION */) : null}
        {renderRow(
          "Enregistrements" /* TRANSLATION */,
          <span className={styles.chips}>
            {Object.values(OvenMediaSubtitlesStatus).map((status) => {
              const count = state.records[status] ?? 0;
              return (
                <span key={status} className={styles.chip} style={{ backgroundColor: getSubtitlesStatusColor(status), opacity: count ? 1 : 0.35 }}>
                  {`${getSubtitlesStatusLabel(status)} : ${count}`}
                </span>
              );
            })}
          </span>
        )}
        {renderRow(
          "File (BullMQ)" /* TRANSLATION */,
          state.queue
            ? Object.entries(state.queue)
                .map(([key, count]) => `${QUEUE_LABELS[key] ?? key} : ${count}`)
                .join(" · ")
            : undefined
        )}
        {renderRow(
          "En cours" /* TRANSLATION */,
          running
            ? `${running.app}/${running.stream} — ${running.piecesDone ?? 0}/${running.piecesTotal ?? "?"} morceau(x), depuis ${new Date(running.startedAt).toLocaleTimeString()}${
                until ? ` — forcé jusqu'à ${until.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}${running.override?.busy ? " (même pendant un flux)" : ""}` : ""
              }` /* TRANSLATION */
            : "aucune transcription" /* TRANSLATION */
        )}
      </>
    );
  };

  return (
    <div className={classes.join(" ")} style={style}>
      <div className={styles.header}>
        <div className={styles.title}>{"SOUS-TITRES" /* TRANSLATION */}</div>
        {showServer ? <div className={styles.server}>{serverId}</div> : null}
      </div>
      <div className={styles.section}>{renderStatic()}</div>
      {block?.configured ? (
        <>
          <div className={styles.section}>{renderLive()}</div>
          <div className={styles.actions}>
            <Touchable className={styles.button} onPress={() => load()}>
              {loading ? "…" : "Actualiser" /* TRANSLATION */}
            </Touchable>
            <Touchable className={[styles.button, styles.buttonWarning].join(" ")} onPress={runAll}>
              {"Tout transcrire maintenant" /* TRANSLATION */}
            </Touchable>
            <Touchable className={[styles.button, styles.buttonDanger].join(" ")} onPress={stop}>
              {"Arrêter" /* TRANSLATION */}
            </Touchable>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default DashboardSubtitlesContainer;
